import { Kind, parse, print } from "graphql";
import bundledGraphqlSchema from "../data/bundledGraphqlSchema";

const SCALAR_TYPES = new Set([
  "String", "Int", "Float", "Boolean", "ID", "JSON",
]);

function unwrapType(typeNode) {
  if (!typeNode) return { name: "Unknown", required: false, list: false };
  if (typeNode.kind === Kind.NON_NULL_TYPE) {
    const inner = unwrapType(typeNode.type);
    return { ...inner, required: true };
  }
  if (typeNode.kind === Kind.LIST_TYPE) {
    const inner = unwrapType(typeNode.type);
    return { ...inner, list: true };
  }
  return {
    name: typeNode.name?.value || "Unknown",
    required: false,
    list: false,
  };
}

function formatType(typeNode) {
  const { name, required, list } = unwrapType(typeNode);
  const base = list ? `[${name}]` : name;
  return required ? `${base}!` : base;
}

function unwrapTypeFromString(typeStr) {
  return (typeStr || "").replace(/[\[\]!]/g, "");
}

function mapField(field) {
  return {
    name: field.name.value,
    type: formatType(field.type),
    baseType: unwrapTypeFromString(formatType(field.type)),
    args: (field.arguments || []).map((arg) => ({
      name: arg.name.value,
      type: formatType(arg.type),
      baseType: unwrapTypeFromString(formatType(arg.type)),
    })),
    description: field.description?.value || null,
  };
}

function mapInputField(field) {
  return {
    name: field.name.value,
    type: formatType(field.type),
    baseType: unwrapTypeFromString(formatType(field.type)),
    description: field.description?.value || null,
  };
}

export function isScalarType(typeName) {
  return SCALAR_TYPES.has(typeName);
}

export function schemaHasContent(tree) {
  if (!tree) return false;
  return (
    tree.queryFields?.length > 0 ||
    tree.mutationFields?.length > 0 ||
    tree.types?.length > 0
  );
}

export function parseGraphqlSchema(sdl) {
  const empty = {
    queryFields: [],
    mutationFields: [],
    types: [],
    enums: [],
    inputs: [],
    typeIndex: {},
    error: null,
  };

  if (!sdl || !sdl.trim()) {
    return empty;
  }

  if (!isValidGraphqlSdl(sdl)) {
    return {
      ...empty,
      error: "SDL inválido: se recibió HTML o contenido que no es GraphQL",
    };
  }

  try {
    const doc = parse(sdl);
    const typesMap = {};
    const enums = [];
    const inputs = [];
    let schemaQuery = "Query";
    let schemaMutation = "Mutation";

    doc.definitions.forEach((def) => {
      if (def.kind === Kind.SCHEMA_DEFINITION) {
        def.operationTypes?.forEach((op) => {
          if (op.operation === "query") schemaQuery = op.type.name.value;
          if (op.operation === "mutation") schemaMutation = op.type.name.value;
        });
        return;
      }

      if (def.kind === Kind.OBJECT_TYPE_DEFINITION) {
        typesMap[def.name.value] = {
          name: def.name.value,
          kind: "OBJECT",
          description: def.description?.value || null,
          fields: (def.fields || []).map(mapField),
        };
        return;
      }

      if (def.kind === Kind.INPUT_OBJECT_TYPE_DEFINITION) {
        inputs.push({
          name: def.name.value,
          kind: "INPUT",
          description: def.description?.value || null,
          fields: (def.fields || []).map(mapInputField),
        });
        return;
      }

      if (def.kind === Kind.ENUM_TYPE_DEFINITION) {
        enums.push({
          name: def.name.value,
          kind: "ENUM",
          description: def.description?.value || null,
          values: (def.values || []).map((v) => v.name.value),
        });
      }
    });

    const queryType = typesMap[schemaQuery];
    const mutationType = typesMap[schemaMutation];

    const queryFields = queryType?.fields || [];
    const mutationFields = mutationType?.fields || [];

    const types = Object.values(typesMap)
      .filter((t) => t.name !== schemaQuery && t.name !== schemaMutation)
      .sort((a, b) => a.name.localeCompare(b.name));

    const typeIndex = {};
    types.forEach((t) => {
      typeIndex[t.name] = t;
    });
    inputs.forEach((t) => {
      typeIndex[t.name] = t;
    });
    enums.forEach((t) => {
      typeIndex[t.name] = t;
    });

    return {
      queryFields,
      mutationFields,
      types,
      enums,
      inputs,
      typeIndex,
      schemaQuery,
      schemaMutation,
      error: null,
    };
  } catch (err) {
    return { ...empty, error: err.message || String(err) };
  }
}

export function getTypeDefinition(schemaTree, typeName) {
  if (!schemaTree?.typeIndex || !typeName) return null;
  return schemaTree.typeIndex[typeName] || null;
}

export function buildFieldSelection(typeName, schemaTree, depth = 0, maxDepth = 2) {
  if (!typeName || depth >= maxDepth || isScalarType(typeName)) return "";
  const type = getTypeDefinition(schemaTree, typeName);
  if (!type?.fields?.length) return "";

  const lines = type.fields.slice(0, 10).map((field) => {
    if (isScalarType(field.baseType) || depth + 1 >= maxDepth) {
      return `    ${field.name}`;
    }
    const nested = buildFieldSelection(field.baseType, schemaTree, depth + 1, maxDepth);
    if (nested) {
      return `    ${field.name} {\n${nested}\n    }`;
    }
    return `    ${field.name}`;
  });

  return lines.join("\n");
}

export function buildOperationQuery(field, kind, schemaTree) {
  const opKind = kind === "MUTATION" ? "mutation" : "query";
  const opName = field.name.charAt(0).toUpperCase() + field.name.slice(1);
  const args = field.args || [];
  const argsStr = args.length
    ? `(\n${args.map((a) => `  ${a.name}: ${defaultArgValue(a.type)}`).join("\n")}\n)`
    : "";

  const returnBase = field.baseType || unwrapTypeFromString(field.type);
  const selection = buildFieldSelection(returnBase, schemaTree);

  if (!selection) {
    return `${opKind} ${opName} {\n  ${field.name}${argsStr}\n}`;
  }

  return `${opKind} ${opName} {\n  ${field.name}${argsStr} {\n${selection}\n  }\n}`;
}

function defaultArgValue(typeStr) {
  if (typeStr.includes("Int")) return "10";
  if (typeStr.includes("Float")) return "0";
  if (typeStr.includes("Boolean")) return "true";
  if (typeStr.includes("ID") || typeStr.includes("String")) return '""';
  return "null";
}

export function isValidGraphqlSdl(text) {
  if (!text || typeof text !== "string") return false;
  const trimmed = text.trim();
  if (!trimmed || trimmed.startsWith("<")) return false;
  return /\btype\s+Query\b/.test(trimmed) || /\btype\s+Mutation\b/.test(trimmed);
}

export function getBundledSchemaSdl() {
  return bundledGraphqlSchema;
}

export function formatGraphqlQuery(source) {
  return print(parse(source));
}

export async function loadFallbackSchemaSdl() {
  // Prefer bundled SDL — fetch often returns SPA index.html on Amplify rewrites.
  if (isValidGraphqlSdl(bundledGraphqlSchema)) {
    return bundledGraphqlSchema;
  }

  const base = process.env.PUBLIC_URL || "";
  const res = await fetch(`${base}/graphql-schema.graphql`, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`No se pudo cargar schema local (HTTP ${res.status})`);
  }
  const text = await res.text();
  if (!isValidGraphqlSdl(text)) {
    throw new Error("Respuesta inválida: se recibió HTML en lugar del schema GraphQL");
  }
  return text;
}
